import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faUsers } from '@fortawesome/pro-regular-svg-icons/faUsers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'antd';
import { AssignToLocationModal } from 'pos-client/components/locations/components/AssignToLocationModal.tsx';
import { useAssignedUsers } from 'pos-client/components/locations/hooks/useAssignedToLocation.tsx';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';

import { UsersList } from './UsersList.tsx';

export const LocationUsers = ({
  isLocationOverview,
}: {
  isLocationOverview?: boolean;
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const canAddAndEditUsers = hasPermissions('add.edit.users');

  const { location } = useOutletContext<{
    location: LocationModel;
  }>();

  const { showSuccess, showError } = useNotifications();

  const trpcContext = trpc.useUtils();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isLoading, locationUsersTableData, dropDownUsers } = useAssignedUsers(
    {
      location,
    }
  );

  const { mutateAsync: linkUsersMutation, isLoading: isLinkUserLoading } =
    trpc.locations.batchLinkUserToLocation.useMutation();

  const handleUserAssign = (selectedUsers: string[]) => {
    if (selectedUsers.length > 0) {
      linkUsersMutation(
        { locationId: location?.id as string, userIds: selectedUsers },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            showSuccess(
              t('common.notification.success.message', {
                action: 'assigned',
                entity: 'users',
              })
            );
            trpcContext.locations.findLocationLinkedUsers.invalidate();
          },
          onError: () => {
            showError(
              t('common.notification.error.message', {
                action: 'assign',
                entity: 'users',
              })
            );
          },
        }
      );
    }
  };

  const actionComponent = () => {
    if (isLocationOverview) {
      return (
        <Button>
          <Link to={`/locations/${location.id}/users`}>
            {t('users.viewUsers')}
          </Link>
        </Button>
      );
    }

    if (canAddAndEditUsers) {
      return (
        <Button
          onClick={() => setIsModalOpen(true)}
          icon={<FontAwesomeIcon icon={faPlus} />}
        >
          {t('common.buttons.addNew')}
        </Button>
      );
    }
  };

  return (
    <>
      <PosCard
        headerProps={{
          title: t('users.users'),
          icon: faUsers,
          action: actionComponent(),
        }}
      >
        <UsersList
          isLocationOverview={isLocationOverview}
          users={locationUsersTableData ?? []}
          loading={isLoading}
        />
      </PosCard>
      <AssignToLocationModal
        open={isModalOpen}
        title={t('users.addUser')}
        contentText={t('users.searchUsers')}
        okText={t('users.addUser')}
        label={t('users.user')}
        loading={isLoading}
        assigning={isLinkUserLoading}
        selectOptions={dropDownUsers}
        handleAssign={handleUserAssign}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
